import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Copy, EllipsisVertical, ExternalLink, Info, Plus, Video } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Pagination } from "@/components/Pagination";
import { Badge } from "@/components/ui/badge";
import { TableSkeleton } from "@/components/ui/Skeleton/TableSkeleton";
import { useGetData } from "@/hooks/useGetData";
import { formatDate, formatDateTime12 } from "@/utils/helpers";

export type TelehealthAppointment = {
  id: string;
  appointmentDate: string;
  timeSlot: string;
  provider: string;
  scheduledBy: string;
  bookedOn: string;
  meetingLink: string;
  transcript: string;
  recordingLink: string;
  status: string;
  notifications: Array<boolean | null>;
};

type Props = {
  enabled: boolean;
  tcmPlanId: string;
  displayName?: string;
  refreshKey: number;
  onRows: (rows: TelehealthAppointment[]) => void;
  onSchedule: () => void;
  onReschedule: (appointment: TelehealthAppointment) => void;
  onCancel: (appointment: TelehealthAppointment) => void;
};

function text(value: unknown) {
  return String(value ?? "").trim();
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function personName(value: unknown) {
  if (!value || typeof value === "string") return text(value);
  if (typeof value !== "object") return "";
  const row = value as Record<string, unknown>;
  const name = [row.firstName, row.lastName].map(text).filter(Boolean).join(" ");
  return name || text(row.name ?? row.fullName);
}

function boolOrNull(value: unknown): boolean | null {
  if (typeof value === "boolean") return value;
  return null;
}

function mapAppointment(raw: unknown): TelehealthAppointment | null {
  const row = asRecord(raw);
  if (!row) return null;
  const id = text(row._id ?? row.id);
  const appointmentDate = text(row.appointmentDate ?? row.date ?? row.scheduledStartAt);
  const timeSlot = text(row.timeSlot);
  if (!id && !appointmentDate && !timeSlot) return null;
  const flags = [
    row.notif1Sent ?? row.n1,
    row.notif2Sent ?? row.n2,
    row.notif3Sent ?? row.n3,
    row.notif4Sent ?? row.n4,
    row.notif5Sent ?? row.n5,
  ].map(boolOrNull);
  return {
    id: id || `${appointmentDate}-${timeSlot}`,
    appointmentDate,
    timeSlot,
    provider: personName(row.hostProvider ?? row.provider ?? row.hostProviderId),
    scheduledBy: personName(row.scheduledBy ?? row.createdBy ?? row.caretaker),
    bookedOn: text(row.patientStartAt),
    meetingLink: text(row.meetingLink ?? row.wherebyUrl ?? row.joinUrl ?? row.roomUrl ?? row.videoLink),
    transcript: text(row.transcript ?? row.transcriptUrl ?? row.transcriptLink),
    recordingLink: text(row.recordingLink ?? row.recordingUrl),
    status: text(row.status),
    notifications: flags,
  };
}

function listFrom(value: unknown): unknown[] | null {
  if (Array.isArray(value)) return value;
  const record = asRecord(value);
  if (!record) return null;
  const nested = [
    record.appointments,
    record.telehealth,
    record.telehealthAppointments,
    record.items,
    record.results,
  ].find(Array.isArray);
  if (Array.isArray(nested)) return nested;
  if (record._id || record.id || record.appointmentDate || record.timeSlot) return [record];
  return null;
}

function extractAppointments(payload: unknown): TelehealthAppointment[] {
  const root = asRecord(payload);
  const buckets = [payload, root?.data, root?.appointments, root?.telehealth];
  for (const bucket of buckets) {
    const list = listFrom(bucket);
    if (list) {
      return list.map(mapAppointment).filter((row): row is TelehealthAppointment => Boolean(row));
    }
  }
  return [];
}

function extractTotal(payload: unknown) {
  const root = asRecord(payload);
  const data = asRecord(root?.data) ?? root;
  const raw = data?.total ?? asRecord(data?.pagination)?.total ?? asRecord(data?.pagination)?.count;
  const total = Number(raw);
  return Number.isFinite(total) ? total : undefined;
}

function displayDate(value: string) {
  const formatted = formatDate(value);
  return formatted ? formatted.replace(/-/g, "/") : "";
}

function slotStart(timeSlot: string) {
  const start = timeSlot.split("-")[0]?.trim();
  return start || "";
}

function bookedParts(value: string) {
  if (!value) return { date: "—", time: "" };
  if (value.includes("T")) {
    const formatted = formatDateTime12(value);
    if (formatted) {
      const [date, time, meridiem] = formatted.split(" ");
      return { date, time: [time, meridiem].filter(Boolean).join(" ") };
    }
  }
  return { date: displayDate(value) || value, time: "" };
}

function statusClass(status: string) {
  const key = status.toLowerCase();
  if (key === "scheduled" || key === "pending") return "border-transparent bg-sky-100 text-sky-700";
  if (key === "completed") return "border-transparent bg-emerald-100 text-emerald-700";
  if (key === "cancelled" || key === "canceled") return "border-transparent bg-slate-100 text-slate-500";
  return "border-transparent bg-slate-100 text-slate-600";
}

function statusLabel(status: string) {
  if (!status) return "—";
  return status.replace(/[_-]+/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function mediaHref(value: string) {
  return /^https?:\/\//i.test(value) ? value : "";
}

function SectionHeader({ onSchedule }: { onSchedule?: () => void }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
      <div className="flex items-center gap-2">
        <Video className="h-4 w-4 text-primary" />
        <h2 className="text-base font-semibold">Telehealth Encounters</h2>
      </div>
      {onSchedule ? (
        <Button type="button" size="sm" onClick={onSchedule}>
          <Plus className="h-4 w-4" />
          Schedule Telehealth
        </Button>
      ) : null}
    </div>
  );
}

export function TcmTelehealthEncounters({
  enabled,
  tcmPlanId,
  displayName = "",
  refreshKey,
  onRows,
  onSchedule,
  onReschedule,
  onCancel,
}: Props) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const params = useMemo(() => ({ page, limit }), [limit, page]);

  const { apiData, loading, err, fetchData } = useGetData(
    `/telehealth/tcm-plans/${tcmPlanId}`,
    params,
    false,
    enabled && !!tcmPlanId,
  );
  const rows = useMemo(() => (enabled ? extractAppointments(apiData) : []), [apiData, enabled]);
  const totalRecords = extractTotal(apiData) ?? rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / limit) || 1);

  useEffect(() => {
    setPage(1);
  }, [tcmPlanId]);

  useEffect(() => {
    onRows(rows);
  }, [onRows, rows]);

  useEffect(() => {
    if (!enabled || refreshKey === 0) return;
    fetchData();
  }, [refreshKey]);

  const joinCall = (link: string) => {
    navigate("/telehealth/room", {
      state: { room: link, displayName },
    });
  };

  const copyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Meeting link copied");
    } catch {
      toast.error("Could not copy the meeting link");
    }
  };

  if (!enabled) {
    return (
      <section className="rounded-lg border bg-white">
        <SectionHeader />
        <div className="flex flex-col items-center px-6 py-16 text-center">
          <Info className="h-8 w-8 text-sky-500" />
          <p className="mt-3 text-sm font-semibold text-[#111827]">Save the TCM plan first</p>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">
            Please save the current changes to the TCM plan before scheduling or viewing telehealth encounters.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-lg border bg-white">
      <SectionHeader onSchedule={onSchedule} />
      {loading ? (
        <TableSkeleton rows={limit} columns={7} showActions />
      ) : (
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>Schedule Date & Time</TableHead>
            <TableHead>Meeting Access</TableHead>
            <TableHead>Provider / Caretaker</TableHead>
            <TableHead>Booked On</TableHead>
            <TableHead>Notification Pipeline (N1-N5)</TableHead>
            <TableHead>Clinical Media</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-12 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {err ? (
            <TableRow>
              <TableCell colSpan={8} className="text-sm text-red-600">
                {err}
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="text-sm text-muted-foreground">
                No telehealth encounters yet.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => {
              const booked = bookedParts(row.bookedOn);
              const scheduleDate = displayDate(row.appointmentDate) || row.appointmentDate;
              const startTime = slotStart(row.timeSlot);
              const transcriptHref = mediaHref(row.transcript);
              const recordingHref = mediaHref(row.recordingLink);
              return (
                <TableRow key={row.id}>
                  <TableCell>
                    <p className="font-medium text-[#111827]">
                      {[scheduleDate, startTime].filter(Boolean).join(", ") || "—"}
                    </p>
                    <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                      {/* <span className="h-1.5 w-1.5 rounded-full bg-sky-500" /> */}
                      Duration: 30 min (Video)
                    </p>
                  </TableCell>
                  <TableCell>
                    {row.meetingLink ? (
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          size="sm"
                          className="h-8 rounded-lg"
                          onClick={() => joinCall(row.meetingLink)}
                        >
                          <Video className="h-3.5 w-3.5" />
                          Join Video Call
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                        {/* <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 rounded-lg p-0"
                          aria-label="Copy meeting link"
                          onClick={() => void copyLink(row.meetingLink)}
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </Button> */}
                      </div>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    <p className="font-medium text-[#111827]">{row.provider || "—"}</p>
                    {row.scheduledBy ? (
                      <p className="mt-1 text-xs text-muted-foreground">By: {row.scheduledBy}</p>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <p className="text-[#111827]">{booked.date}</p>
                    {booked.time ? (
                      <p className="mt-1 text-xs text-muted-foreground">{booked.time}</p>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-muted-foreground">—</TableCell>
                  <TableCell>
                    {transcriptHref || recordingHref || row.transcript || row.recordingLink ? (
                      <div className="flex flex-col gap-1">
                        {row.transcript ? (
                          transcriptHref ? (
                            <a
                              href={transcriptHref}
                              target="_blank"
                              rel="noreferrer"
                              className="text-sm text-primary hover:underline"
                            >
                              View Transcript
                            </a>
                          ) : (
                            <span className="text-sm text-primary">View Transcript</span>
                          )
                        ) : null}
                        {row.recordingLink ? (
                          recordingHref ? (
                            <a
                              href={recordingHref}
                              target="_blank"
                              rel="noreferrer"
                              className="text-sm text-primary hover:underline"
                            >
                              Recording
                            </a>
                          ) : (
                            <span className="text-sm text-primary">Recording</span>
                          )
                        ) : null}
                      </div>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={`teachers capitalize rounded-full px-5 py-1 font-medium ${statusClass(row.status)}`}>
                      {statusLabel(row.status)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {row.status.toLowerCase() === "scheduled" ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            aria-label="Encounter actions"
                          >
                            <EllipsisVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => onReschedule(row)}>
                            Reschedule
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => onCancel(row)}>
                            Cancel appointment
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      ""
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      )}
      {!loading ? (
      <div className="border-t px-4 py-3">
        <Pagination
          page={page}
          setPage={setPage}
          limit={limit}
          setLimit={setLimit}
          total={totalPages}
          pageSizeOptions={[10, 20, 50]}
        />
      </div>
      ) : null}
    </section>
  );
}
